import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Bot, ShieldCheck, ExternalLink, Server } from "lucide-react";
import { publicAgentApi } from "../lib/ledgerApi";

/**
 * Public agent profile — Phase 6 polish.
 * NEVER shows policy, keys, or per-tx amounts. ONLY trust signals.
 */
export default function PublicAgent() {
  const { did } = useParams();
  const [agent, setAgent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    publicAgentApi
      .get(did)
      .then((a) => alive && setAgent(a))
      .catch((e) =>
        alive && setError(e?.response?.data?.detail || e.message || "agent not found")
      );
    return () => {
      alive = false;
    };
  }, [did]);

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="public-agent-page">
      <Link to="/" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back to explorer
      </Link>

      <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">/ Agent · Public profile</div>

      {error && (
        <div className="border border-status-tampered/40 bg-status-tampered/10 p-4 rounded-sm text-status-tampered text-sm">
          {error}
        </div>
      )}

      {agent && (
        <>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-medium mb-2 flex items-center gap-3" data-testid="public-agent-display-name">
            <Bot className="w-7 h-7 text-muted-foreground" strokeWidth={1.5} />
            {agent.display_name || "Agent"}
          </h1>
          <div className="font-mono text-xs text-muted-foreground break-all mb-6" data-testid="public-agent-did" title={agent.did}>
            {agent.did}
          </div>

          <div className="border border-border rounded-sm bg-card p-5 md:p-6 mb-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm font-mono">
              <Cell label="Status">
                <StatusPill s={agent.status} />
              </Cell>
              <Cell label="Parent business" value={agent.parent_business_display_name || "—"} testid="public-agent-business" />
              <Cell label="Created" value={agent.created_at?.slice(0, 10) || "—"} />
              <Cell label="Lifetime payments" value={agent.lifetime_payment_count} testid="public-agent-lifetime" />
              <Cell label="Last 24h volume" value={`${Number(agent.last_24h_volume_usd || 0).toFixed(2)} USD`} testid="public-agent-volume" />
              <Cell label="Pubkey FP">
                <span className="text-foreground">{agent.network_pubkey_fingerprint || "—"}</span>
              </Cell>
            </div>
          </div>

          <div className="border border-border rounded-sm bg-card p-5 md:p-6 mb-6 flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-status-ok mt-0.5" strokeWidth={1.5} />
            <div className="text-sm">
              <div className="font-medium mb-1">Cryptographically verifiable</div>
              <div className="text-muted-foreground">
                Every payment by this agent is signed (server-custodial ECDSA secp256k1)
                and committed by NeoPay's validator federation. Any third party can
                reproduce a transaction's inclusion proof without trusting our API.
              </div>
              <Link
                to="/validators"
                className="mt-3 inline-flex items-center gap-1 text-xs underline text-foreground"
                data-testid="public-agent-validators-link"
              >
                <Server className="w-3 h-3" strokeWidth={1.5} /> See the validator federation
              </Link>
            </div>
          </div>

          <div className="text-[11px] text-muted-foreground font-mono">
            This is a public, read-only profile. No policy, keys, or per-transaction
            amounts are exposed.
          </div>
        </>
      )}
    </div>
  );
}

function Cell({ label, value, testid, children }) {
  return (
    <div data-testid={testid} className="border border-border rounded-sm px-3 py-2 bg-background">
      <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground mb-0.5">{label}</div>
      <div>{children ?? value ?? "—"}</div>
    </div>
  );
}

function StatusPill({ s }) {
  const map = {
    active: "border-status-ok/40 bg-status-ok/10 text-status-ok",
    paused: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400",
    revoked: "border-status-tampered/40 bg-status-tampered/10 text-status-tampered",
  };
  return (
    <span className={`px-1.5 py-0.5 border rounded-sm text-[10px] uppercase tracking-widest ${map[s] || "border-border"}`}>
      {s || "unknown"}
    </span>
  );
}
